<template>
  <div>
    <Head :title="__('Users')" />

    <div class="bg-white rounded-xl shadow-sm border border-gray-100 mb-4 ticket-filters-card">
      <div class="flex flex-col md:flex-row gap-3 p-4 justify-between items-center ticket-filters">
        <search-input v-model="form.search" class="w-full max-w-md" @reset="reset" />
        <div class="filter-add-new flex flex-col gap-3 md:flex-row items-center">
          <Link :href="route('pending_users')" class="relative btn-secondary flex items-center gap-2">
            <icon name="users" class="w-4 h-4" />
            <span>{{ __('Pending Users') }}</span>
            <span v-if="pendingCount > 0" class="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full min-w-[1.25rem] h-5 flex items-center justify-center px-1 leading-none">
              {{ pendingCount }}
            </span>
          </Link>
          <Link class="btn-indigo" :href="route('users.create')">
            <span>{{ __('Create New') }}</span>
          </Link>
        </div>
      </div>

      <div class="filters-toggle flex items-center justify-between px-4 py-3 border-t" @click="showFilters = !showFilters">
        <span class="text-sm font-medium flex items-center gap-2">
          {{ activeFiltersCount }} {{ __('filters active') }}
        </span>
        <icon name="cheveron-down" class="w-4 h-4 filters-toggle-icon" :class="{ 'is-open': showFilters }" />
      </div>

      <transition name="accordion">
        <div v-show="showFilters" class="filters-body px-4 pb-4 border-t">
          <div class="flex flex-wrap gap-2 items-center pt-3">
            <span class="text-sm font-medium whitespace-nowrap">{{ __('Filter By') }}:</span>

            <select-input v-model="form.role_id" class="ticket-filter-item">
              <option :value="null">{{ __('Role') }}</option>
              <option v-for="(r, ri) in roles" :key="ri" :value="r.id">{{ r.name }}</option>
            </select-input>

            <select-input v-model="form.department_id" class="ticket-filter-item">
              <option :value="null">{{ __('Department') }}</option>
              <option v-for="(d, di) in departments" :key="di" :value="d.id">{{ d.name }}</option>
            </select-input>

            <select-input v-model="form.is_active" class="ticket-filter-item">
              <option :value="null">{{ __('Status') }}</option>
              <option value="1">{{ __('Active') }}</option>
              <option value="0">{{ __('Inactive') }}</option>
            </select-input>
          </div>
        </div>
      </transition>
    </div>

    <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
      <table class="w-full whitespace-nowrap ticket_list">
        <tr class="text-left font-bold">
          <th>{{ __('Name') }}</th>
          <th>{{ __('Email') }}</th>
          <th>{{ __('Department') }}</th>
          <th>{{ __('Channel') }}</th>
          <th>{{ __('Role') }}</th>
          <th v-if="$page.props.auth.user.role.slug === 'super_admin'">{{ __('Last Login') }}</th>
          <th colspan="2">{{ __('Status') }}</th>
        </tr>
        <tr v-for="user in users.data" :key="user.id" class="hover:bg-gray-100 focus-within:bg-gray-100 ticket-row">
          <td class="border-t">
            <Link class="flex items-center gap-2 px-4 py-2 focus:text-indigo-500" :href="route('users.edit', user.id)">
              <div class="um-avatar">
                <img v-if="user.photo" :src="user.photo" class="w-full h-full object-cover" />
                <span v-else class="um-avatar-initials">{{ (user.first_name || '?')[0].toUpperCase() }}</span>
              </div>
              <span class="text-sm font-medium">{{ properName(user.name) }}</span>
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-4 py-2 text-sm" :href="route('users.edit', user.id)" tabindex="-1">
              {{ user.email }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-4 py-2 text-sm" :href="route('users.edit', user.id)" tabindex="-1">
              {{ __(user.department) }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-4 py-2 text-sm" :href="route('users.edit', user.id)" tabindex="-1">
              {{ __(user.channel) }}
            </Link>
          </td>
          <td class="border-t capitalize">
            <Link class="flex items-center px-4 py-2 text-sm" :href="route('users.edit', user.id)" tabindex="-1">
              {{ user.role ? user.role.name : '—' }}
            </Link>
          </td>
          <td v-if="$page.props.auth.user.role.slug === 'super_admin'" class="border-t">
            <Link class="flex items-center px-4 py-2 text-sm text-gray-500" :href="route('users.edit', user.id)" tabindex="-1">
              {{ user.last_login ? moment(user.last_login).fromNow() : '—' }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-4 py-2" :href="route('users.edit', user.id)" tabindex="-1">
              <span :class="user.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'" class="px-2 py-0.5 rounded-full text-xs font-semibold">
                {{ user.is_active ? __('Active') : __('Inactive') }}
              </span>
            </Link>
          </td>
          <td class="w-px border-t">
            <Link class="flex items-center px-3 py-2" :href="route('users.edit', user.id)" tabindex="-1">
              <icon name="cheveron-right" class="block w-5 h-5 fill-gray-400" />
            </Link>
          </td>
        </tr>
        <tr v-if="users.data.length === 0">
          <td class="px-4 py-3 border-t text-sm text-gray-500" colspan="7">No users found.</td>
        </tr>
      </table>
    </div>
      <pagination class="mt-6" :links="users.links" />
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/vue3'
import moment from 'moment'
import Icon from '@/Shared/Icon'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import Pagination from '@/Shared/Pagination'
import SearchInput from '@/Shared/SearchInput'
import SelectInput from '@/Shared/SelectInput'

export default {
  components: {
      SearchInput,
    Head,
    Icon,
    Link,
      SelectInput,
      Pagination,
  },
  layout: Layout,
  props: {
    filters: Object,
    users: Object,
    roles: Array,
    departments: Array,
    pendingCount: Number,
  },
  data() {
    return {
      showFilters: true,
      form: {
        search: this.filters.search,
        role_id: this.filters.role_id ?? null,
        department_id: this.filters.department_id ?? null,
        is_active: this.filters.is_active ?? null,
      },
    }
  },
  computed: {
    activeFiltersCount() {
      const keys = ['role_id', 'department_id', 'is_active']
      return keys.filter((key) => this.form[key] !== null && this.form[key] !== '' && this.form[key] !== undefined).length
    },
  },
  watch: {
    form: {
      deep: true,
      handler: throttle(function () {
        this.$inertia.get(this.route('users'), pickBy(this.form), { preserveState: true })
      }, 150),
    },
  },
  methods: {
    reset() {
      this.form = mapValues(this.form, () => null)
    },
  },
}
</script>
